// src/App.js - Version avec système d'abonnement Terrilink
import React, { Suspense, useEffect, useState, useCallback, useMemo } from 'react'
import { BrowserRouter, Route, Routes, Navigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector, Provider } from 'react-redux'
import { NotificationProvider } from './contexts/NotificationContext'
import { useColorModes } from '@coreui/react'
import { ModuleProvider } from './contexts/ModuleContext'
import { EntitlementsProvider } from './contexts/EntitlementsContext'
import ModuleRoute from './components/ModuleRoute'
import { OrganizationProvider } from './contexts/OrganizationContext'
import { LanguageProvider } from './contexts/LanguageContext'
import { SocketProvider } from './contexts/SocketContext'
import { AccommodationProvider } from './contexts/AccommodationContext'
import { OnlineStatusProvider } from './contexts/OnlineStatusContext'
import ErrorBoundary from './components/ErrorBoundary'
import AccountStatusWrapper from './layout/AccountStatusWrapper'
import LoadingScreen from './components/LoadingScreen'
import { fetchUser } from './store/user/userActions'
import { useIsModuleEnabled } from './hooks/useModuleAccess'
// ✅ P1: setupAxiosInterceptors supprimé - logique consolidée dans apiClient.js
import { checkTenantExists } from './services/tenantService'
import { getTenantSlug } from './utils/tenant'
import store from './store/store'
import routes from './routes'

// Import du nouveau système de messagerie flottante
import FloatingMessagingManager from './components/messaging/FloatingMessagingManager'

// Import du système de navigation mobile/PWA
import BottomNavigation from './components/BottomNavigation'
import useIsPWA from './hooks/useIsPWA'

// Import du prompt de mise à jour PWA
import PWAUpdatePrompt from './components/PWAUpdatePrompt'
import PWANotificationPrompt from './components/PWANotificationPrompt'
import DocumentTitle from './components/DocumentTitle'
import InstallAppWall from './components/InstallAppWall'

// ✅ FIX DEEP-LINKING: Handler pour ouvrir une conversation depuis une notification push
import PushDeepLinkHandler from './components/PushDeepLinkHandler'

import './scss/style.scss'
import './scss/examples.scss'
import './styles/tabs.scss'
import 'react-datepicker/dist/react-datepicker.css'
import './views/accommodations/accommodations.css'

// Importation des composants en lazy
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'))
const Login = React.lazy(() => import('./views/pages/login/Login'))
const GoogleSsoFinish = React.lazy(() => import('./views/pages/login/GoogleSsoFinish'))
const Register = React.lazy(() => import('./views/pages/register/Register'))
const Page404 = React.lazy(() => import('./views/pages/page404/Page404'))
const Page500 = React.lazy(() => import('./views/pages/page500/Page500'))
const TenantNotFound = React.lazy(() => import('./views/pages/TenantNotFound/TenantNotFound'))
const Privacy = React.lazy(() => import('./views/pages/privacy/Privacy'))

// Pages d'onboarding (publiques)
const RegisterOrganization = React.lazy(
  () => import('./views/pages/onboarding/RegisterOrganization'),
)
const EmailValidationPending = React.lazy(
  () => import('./views/pages/onboarding/EmailValidationPending'),
)
const ValidateEmail = React.lazy(() => import('./views/pages/onboarding/ValidateEmail'))
const ChoosePlan = React.lazy(() => import('./views/pages/onboarding/ChoosePlan'))
const OnboardingSuccess = React.lazy(() => import('./views/pages/onboarding/OnboardingSuccess'))

// Pages RCP publiques (accès via lien tokenisé envoyé par mail / résultats publics)
const PublicSurveyResponseForm = React.lazy(
  () => import('./views/rcpSurveys/PublicSurveyResponseForm'),
)
const PublicInsertionResults = React.lazy(() => import('./views/rcpSurveys/PublicInsertionResults'))
const PublicUnsubscribePage = React.lazy(() => import('./views/rcpSurveys/PublicUnsubscribePage'))

// Page publique de confirmation graduation (consommée depuis l'email J+1)
const GraduationConfirm = React.lazy(() => import('./views/lifecycle/GraduationConfirm'))

// Page publique de confirmation suppression RGPD (consommée depuis l'email
// de confirmation envoyé par DELETE /api/members/me, TTL 7 jours)
const ConfirmDeletion = React.lazy(() => import('./views/account/ConfirmDeletion'))

// Module Recruteur (commit 7a — foundations)
// Auth SÉPARÉE des members (cookie httpOnly distinct `recruiter_auth_token`,
// state via `RecruiterAuthContext` indépendant du Redux store member).
// Le sub-router gère son propre layout + protected routes + page module disabled.
import { RecruiterAuthProvider } from './contexts/RecruiterAuthContext'
const RecruiterRoutes = React.lazy(() => import('./views/recruiters/RecruiterRoutes'))

// Mode présentation réunion — plein écran, monté HORS DefaultLayout (projecteur).
const ClubMeetingPresentation = React.lazy(() => import('./views/clubMeetings/PresentationMode'))

const routerFuture = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
}

// Routes super-admin (rendues hors DefaultLayout, avec AdminLayout dédié).
// Toute autre route /admin/* est traitée comme admin TENANT et hérite du DefaultLayout.
// Note : /admin/deploy-jobs/:id n'est pas listé ici car le matcher exact ne capturerait pas
// le param ; il est géré par le filter route.path?.startsWith en complément ci-dessous.
const SUPER_ADMIN_PATHS = new Set([
  '/admin',
  '/admin/login',
  '/admin/organizations',
  '/admin/webhooks',
  '/admin/system',
  '/admin/deploy-jobs',
  '/admin/deploy-jobs/:id',
])

const AppContent = () => {
  const dispatch = useDispatch()
  const { isColorModeSet, setColorMode } = useColorModes('coreui-free-react-admin-template-theme')
  const storedTheme = useSelector((state) => state.theme)
  const currentUser = useSelector((state) => state.user?.user)

  // Vérifier si le module messagerie est activé
  const isMessagingEnabled = useIsModuleEnabled('messaging')

  // Détection PWA/mobile pour bottom navigation
  const { shouldShowBottomNav, isPWA, isMobile } = useIsPWA()

  // Routes du module recruteur : auth + UI séparées du flow member.
  // Les widgets globaux côté member (messaging dock, bottom nav, push handler)
  // ne doivent PAS s'afficher dessus, même si le user est par ailleurs
  // authentifié comme member dans le même browser.
  const { pathname } = useLocation()
  const isRecruiterRoute = pathname.startsWith('/recruteurs')

  const [isInitializing, setIsInitializing] = useState(true)
  const [sidebarShow, setSidebarShow] = useState(true)
  const [sidebarUnfoldable, setSidebarUnfoldable] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(null)
  // ✅ IMPORTANT: exists: null (pas true!) pour éviter le rendu prématuré des routes
  const [tenantStatus, setTenantStatus] = useState({
    checked: false,
    exists: null,
    errorCode: null,
  })

  // ✅ P1: Interceptors Axios consolidés dans apiClient.js (plus besoin de setupAxiosInterceptors)

  // Vérification du tenant au démarrage (avant toute autre initialisation)
  useEffect(() => {
    const verifyTenant = async () => {
      const slug = getTenantSlug()

      // Pas de slug = pas sur un tenant (localhost sans header, app.terrilink.io, etc.)
      if (!slug) {
        console.log('🏠 Pas de tenant détecté (domaine principal ou localhost)')
        setTenantStatus({ checked: true, exists: true, noTenant: true })
        return
      }

      console.log(`🔍 Vérification du tenant: ${slug}`)

      try {
        const result = await checkTenantExists()

        if (!result.exists) {
          console.error(`❌ Tenant introuvable: ${slug} (${result.errorCode})`)
          setTenantStatus({ checked: true, exists: false, errorCode: result.errorCode })
        } else {
          setTenantStatus({ checked: true, exists: true })
        }
      } catch (error) {
        console.error('❌ Erreur vérification tenant:', error)
        // En cas d'erreur réseau, on laisse passer (le backend gérera)
        setTenantStatus({ checked: true, exists: true, checkFailed: true })
      }
    }

    verifyTenant()
  }, [])

  // ✅ SECURITY: Fonction pour vérifier si l'utilisateur semble connecté
  // La vraie validation se fait côté serveur via le cookie httpOnly
  const checkAuthValidity = useCallback(() => {
    const userId = localStorage.getItem('userId')

    if (!userId) {
      return false
    }

    // Note: L'expiration du token est gérée côté serveur
    // Le serveur retournera 401 si le cookie a expiré
    // apiClient intercepte les 401 et redirige vers /login
    return true
  }, [])

  // ✅ SECURITY: Vérifier via userId (pas de token JS)
  const shouldShowMessaging = useMemo(() => {
    const hasUserId = !!localStorage.getItem('userId')
    return (
      isAuthenticated && currentUser && hasUserId && isMessagingEnabled && !isRecruiterRoute
    )
  }, [isAuthenticated, currentUser, isMessagingEnabled, isRecruiterRoute])

  // ✅ SECURITY: Surveiller périodiquement l'état d'authentification
  // Note: L'expiration du cookie est gérée côté serveur
  useEffect(() => {
    if (isAuthenticated) {
      const interval = setInterval(() => {
        if (!checkAuthValidity()) {
          console.log('🔓 Session invalide, déconnexion automatique')
          setIsAuthenticated(false)
        }
      }, 30000)

      return () => clearInterval(interval)
    }
  }, [isAuthenticated, checkAuthValidity])

  // Initialisation de l'app - ATTEND que la vérification tenant soit terminée
  useEffect(() => {
    const initializeApp = async () => {
      // ✅ IMPORTANT: Ne pas initialiser l'auth si le tenant n'existe pas
      // Cela évite de charger les routes protégées pour un tenant supprimé
      if (!tenantStatus.checked) {
        return // Attendre que le tenant soit vérifié
      }

      if (!tenantStatus.exists) {
        // Tenant supprimé/suspendu - ne pas initialiser l'auth
        setIsInitializing(false)
        return
      }

      try {
        // ✅ SECURITY: Vérifier via userId (le cookie httpOnly gère l'auth)
        if (!checkAuthValidity()) {
          setIsAuthenticated(false)
          setIsInitializing(false)
          return
        }

        await dispatch(fetchUser())
        setIsAuthenticated(true)
      } catch (error) {
        console.error("Erreur lors de l'initialisation:", error)
        setIsAuthenticated(false)
        // ✅ SECURITY: Nettoyer localStorage (préserver données admin)
        const adminUser = localStorage.getItem('adminUser')
        const adminUserId = localStorage.getItem('adminUserId')
        const lang = localStorage.getItem('terrilink_lang')
        localStorage.clear()
        if (adminUser) localStorage.setItem('adminUser', adminUser)
        if (adminUserId) localStorage.setItem('adminUserId', adminUserId)
        if (lang) localStorage.setItem('terrilink_lang', lang)
        document.dispatchEvent(new CustomEvent('user_logged_out'))
      } finally {
        setIsInitializing(false)
      }
    }
    initializeApp()
  }, [dispatch, checkAuthValidity, tenantStatus.checked, tenantStatus.exists])

  // Gestion du thème
  useEffect(() => {
    // Forcer le thème clair et supprimer toute trace de mode sombre
    document.documentElement.removeAttribute('data-coreui-theme')
    document.documentElement.classList.remove('dark-theme', 'dark')
    document.body.classList.remove('dark-theme', 'dark')

    // Nettoyer le localStorage
    localStorage.removeItem('coreui-free-react-admin-template-theme')
    localStorage.removeItem('theme')

    // Forcer le mode clair dans le hook useColorModes
    setColorMode('light')

    // Le reste de votre code existant pour les URL params...
    const urlParams = new URLSearchParams(window.location.href.split('?')[1])
    const theme = urlParams.get('theme') && urlParams.get('theme').match(/^[A-Za-z0-9\s]+/)[0]

    // Ne permettre que le thème 'light'
    if (theme && theme === 'light') {
      setColorMode('light')
    } else {
      setColorMode('light') // Toujours forcer light
    }
  }, [isColorModeSet, setColorMode, storedTheme])

  // Gestion post-login
  useEffect(() => {
    const loginSuccess = localStorage.getItem('login_success')

    if (loginSuccess === 'true') {
      console.log('App: Connexion réussie détectée, initialisation post-login')
      localStorage.removeItem('login_success')

      store
        .dispatch(fetchUser())
        .then(() => {
          console.log('App: Données utilisateur chargées avec succès')
          setTimeout(() => {
            console.log('App: Émission auth_updated')
            document.dispatchEvent(new CustomEvent('auth_updated'))
          }, 500)
        })
        .catch((error) => {
          console.error('App: Erreur chargement utilisateur', error)
        })
    }
  }, [])

  // Surveiller les changements d'authentification
  useEffect(() => {
    if (!currentUser && isAuthenticated) {
      console.log('🔓 currentUser devenu null, possible déconnexion automatique')
      setIsAuthenticated(false)
      document.dispatchEvent(new CustomEvent('user_logged_out'))
    }
  }, [currentUser, isAuthenticated])

  // Surveiller les changements du module messagerie
  useEffect(() => {
    if (isAuthenticated && currentUser) {
      console.log('📡 État du module messagerie:', isMessagingEnabled)

      // Si le module est désactivé, émettre un événement pour nettoyer la messagerie
      if (!isMessagingEnabled) {
        console.log('🚫 Module messagerie désactivé, nettoyage de la messagerie flottante')
        document.dispatchEvent(new CustomEvent('messaging_module_disabled'))
      }
    }
  }, [isMessagingEnabled, isAuthenticated, currentUser])

  // Affichage du spinner pendant la vérification du tenant
  if (!tenantStatus.checked) {
    return <LoadingScreen message="Vérification..." />
  }

  // Si le tenant n'existe pas (explicitement false), afficher la page TenantNotFound
  if (tenantStatus.exists === false) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <TenantNotFound errorCode={tenantStatus.errorCode} />
      </Suspense>
    )
  }

  // Affichage du spinner pendant l'initialisation de l'auth
  if (isInitializing || isAuthenticated === null) {
    return <LoadingScreen message="Initialisation..." />
  }

  return (
    <div className="app-container">
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Routes publiques (sans authentification) */}
          <Route exact path="/login" element={<Login />} />
          {/* Atterrissage SSO Google : PUBLIQUE et déclarée AVANT le gate
              `!isAuthenticated`. À l'arrivée, localStorage.userId n'est pas
              encore posé (la page va justement l'hydrater depuis le cookie
              bootstrap), donc sans route explicite ici le catch-all renverrait
              vers /login et l'hydratation n'aurait jamais lieu. */}
          <Route exact path="/auth/google/finish" element={<GoogleSsoFinish />} />
          {/* Atterrissage SSO LinkedIn : même composant (provider-agnostique, il lit
              le cookie sso_bootstrap posé par n'importe quel provider). */}
          <Route exact path="/auth/linkedin/finish" element={<GoogleSsoFinish />} />
          <Route exact path="/register" element={<Register />} />
          <Route exact path="/404" element={<Page404 />} />
          <Route exact path="/500" element={<Page500 />} />
          <Route exact path="/privacy" element={<Privacy />} />

          {/* Routes d'onboarding (publiques) */}
          <Route exact path="/register-organization" element={<RegisterOrganization />} />
          <Route exact path="/email-validation-pending" element={<EmailValidationPending />} />
          <Route exact path="/validate-email/:token" element={<ValidateEmail />} />
          <Route exact path="/choose-plan" element={<ChoosePlan />} />
          <Route exact path="/onboarding/success" element={<OnboardingSuccess />} />

          {/* Routes RCP publiques (lien mail tokenisé / résultats agrégés) —
              déclarées AVANT la redirection !isAuthenticated pour échapper au gate.
              ORDRE IMPORTANT : la route /rcp/r/:token/unsubscribe doit être
              déclarée AVANT /rcp/r/:token sinon "unsubscribe" est matché comme
              valeur de :token. */}
          <Route path="/rcp/r/:token/unsubscribe" element={<PublicUnsubscribePage />} />
          <Route path="/rcp/r/:token" element={<PublicSurveyResponseForm />} />
          <Route path="/insertion-results/:surveyId" element={<PublicInsertionResults />} />

          {/* Lifecycle student → alumni — page publique consommée depuis l'email J+1 */}
          <Route path="/graduation-confirm" element={<GraduationConfirm />} />

          {/* RGPD article 17 — page publique consommée depuis l'email de confirmation
              suppression (TTL 7 jours, déclenche anonymizeMember côté backend) */}
          <Route path="/account/confirm-deletion" element={<ConfirmDeletion />} />

          {/* Routes super-admin (système d'auth séparé via AdminProtectedRoute + AdminLayout).
              Liste explicite : ce sont les seules routes /admin/* qui sortent du DefaultLayout
              tenant. Les autres /admin/* (ex: /admin/promotions, /admin/programs) sont des
              pages d'admin TENANT et doivent hériter du DefaultLayout. */}
          {routes
            .filter((route) => SUPER_ADMIN_PATHS.has(route.path))
            .map((route, idx) => (
              <Route key={`admin-${idx}`} path={route.path} element={route.element} />
            ))}

          {/* Module Recruteur — auth séparée des members.
              Bypass le gate `!isAuthenticated` (le sub-router gère sa propre auth
              via `RecruiterAuthProvider` + `RecruiterProtectedRoute`).
              Le `RecruiterAuthProvider` est scopé ici pour ne pas faire de fetch
              /me inutile sur les pages member. */}
          <Route
            path="/recruteurs/*"
            element={
              <RecruiterAuthProvider>
                <RecruiterRoutes />
              </RecruiterAuthProvider>
            }
          />

          {/* Mode présentation réunion — plein écran, HORS DefaultLayout (projecteur).
              Route standalone : ModuleRoute assure auth (ProtectedRoute) + gating
              club_meetings, SANS chrome. Volontairement absente de routes.js pour
              ne pas hériter du DefaultLayout. Déclarée avant le "*" : react-router
              v6 la classe plus spécifique, elle gagne. */}
          {isAuthenticated && (
            <Route
              path="/club-meetings/:eventId/presentation"
              element={
                <ModuleRoute moduleName="club_meetings">
                  <ClubMeetingPresentation />
                </ModuleRoute>
              }
            />
          )}

          {!isAuthenticated ? (
            <Route path="*" element={<Navigate to="/login" replace />} />
          ) : (
            /* Routes protégées (nécessitent authentification) */
            <Route
              path="*"
              element={
                <AccountStatusWrapper>
                  <DefaultLayout
                    sidebarShow={sidebarShow}
                    setSidebarShow={setSidebarShow}
                    sidebarUnfoldable={sidebarUnfoldable}
                    setSidebarUnfoldable={setSidebarUnfoldable}
                  />
                </AccountStatusWrapper>
              }
            >
              <Route index element={<Navigate to="/publications" replace />} />

              {routes.map((route, idx) => {
                // Exclure les routes déjà définies
                if (
                  ['/login', '/register', '/404', '/500', '/subscription-required'].includes(
                    route.path,
                  )
                ) {
                  return null
                }

                // Exclure UNIQUEMENT les vraies routes super-admin (rendues hors DefaultLayout
                // avec leur propre AdminLayout). Les routes /admin/* du tenant (promotions,
                // programs, ...) restent dans le DefaultLayout standard.
                if (SUPER_ADMIN_PATHS.has(route.path)) {
                  return null
                }

                // Filtrer la route /messaging si elle existe encore
                if (route.path === '/messaging' || route.path === 'messaging') {
                  return null
                }

                const path = route.path.startsWith('/') ? route.path.slice(1) : route.path
                return route.element ? (
                  <Route key={idx} path={path} element={route.element} />
                ) : null
              })}

              {/* Route 404 pour les routes inconnues */}
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Route>
          )}
        </Routes>
      </Suspense>

      {/* Messagerie flottante (si module activé et utilisateur connecté) */}
      {shouldShowMessaging && <FloatingMessagingManager />}

      {/* ✅ FIX DEEP-LINKING: Handler pour ouvrir une conversation depuis une notification push */}
      {shouldShowMessaging && <PushDeepLinkHandler isReady={shouldShowMessaging} />}

      {/* Bottom Navigation pour mobile (si authentifié, hors module recruteur) */}
      {isAuthenticated && shouldShowBottomNav && !isRecruiterRoute && <BottomNavigation />}

      {/* PWA Update Prompt (toujours affiché pour gérer les mises à jour) */}
      <PWAUpdatePrompt />

      {/* PWA Notification Prompt (première ouverture en mode standalone) */}
      <PWANotificationPrompt />

      {/* Mur souple d'installation PWA : mobile web uniquement, après auth,
          hors module recruteur, jamais en PWA installée */}
      <InstallAppWall
        active={isAuthenticated && isMobile && !isPWA && !isRecruiterRoute}
      />
    </div>
  )
}

const App = () => {
  return (
    <Provider store={store}>
      <BrowserRouter future={routerFuture}>
        <OrganizationProvider>
          {/* Titre de l'onglet : nom de l'organisation + compteur de messages
              non lus. Écrivain unique de document.title (cf.
              OrganizationContext). Monté ICI et pas dans AppContent, qui a
              trois sorties anticipées (vérification du tenant, tenant
              introuvable, initialisation de l'auth) : le titre doit rester
              correct pendant ces phases. */}
          <DocumentTitle />
          <LanguageProvider>
            <ModuleProvider>
              <EntitlementsProvider>
                <NotificationProvider>
                  <SocketProvider>
                    <OnlineStatusProvider>
                      <AccommodationProvider>
                        <ErrorBoundary>
                          <AppContent />
                        </ErrorBoundary>
                      </AccommodationProvider>
                    </OnlineStatusProvider>
                  </SocketProvider>
                </NotificationProvider>
              </EntitlementsProvider>
            </ModuleProvider>
          </LanguageProvider>
        </OrganizationProvider>
      </BrowserRouter>
    </Provider>
  )
}

export default App
